import { PaymentStatus } from '@/enums';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { store } from '@/store';
import { InitPaymentRequest } from '@/types/Payment';
import { UpdatePaymentRequest } from '@/types/Payment/UpdatePayment';
import { PlanSummaryState } from '@/types/PlanSummary';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const getPaymentType = createAsyncThunk(
  'payment/getPaymentType',
  async ({ entityType }: { entityType: number }) => {
    try {
      const rootState = store.getState();
      const affiliate = rootState.affiliate;

      let queryString;
      const agentCode = affiliate.agentCode || '';
      if (!affiliate.agentId) {
        queryString = `?agentCode=${agentCode}%entityType=${entityType}`;
      } else {
        queryString = `?agentCode=${agentCode}%entityType=${entityType}%affiliateId=${affiliate.agentId}`;
      }

      const relativeUrl = `Payment/GetPaymentType`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

const createReferenceNumber = (encodedOrderId: string) => {
  return encodedOrderId ? encodedOrderId + '_' + Date.now().toString() : '';
};

const getTotalAmount = (planSummary: PlanSummaryState) => {
  const total = planSummary.planPrice?.totalPremium
    ? planSummary.planPrice?.totalPremium
    : 0;
  return total;
};

export const updatePayment = (status: PaymentStatus, errorCode: number) => {
  return async () => {
    try {
      const request: UpdatePaymentRequest = {
        orderId: '',
        status,
        errorCode,
        transactionNo: '',
      };
      const relativeUrl = 'Payment/Update';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  };
};

export const getPaymentTransactionNumber = createAsyncThunk(
  'payment/getPaymentTransactionNumber',
  async (encodedOrderId: string) => {
    try {
      const queryString = `?encodedOrderId=${encodedOrderId}`;
      const relativeUrl = 'Payment/GetPaymentTransactionNumber';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const initPayment = createAsyncThunk(
  'payment/init',
  async ({
    description,
    homeType,
    entityType,
    language,
  }: {
    description: string;
    homeType: number;
    entityType: number;
    language: string;
  }) => {
    try {
      const rootState = store.getState();
      const planSummary = rootState.planSummary;
      const personalDetail = rootState.infoState.personalDetail;
      const request: InitPaymentRequest = {
        referenceNo: createReferenceNumber(
          planSummary.encodedOrderId,
        ).substring(0, 60),
        paymentType: planSummary.payMode,
        amount: getTotalAmount(planSummary),
        description: description,
        customerName: personalDetail.name,
        email: personalDetail.email,
        phone: personalDetail.mobileNo,
        encodedOrderId: planSummary.encodedOrderId,
        homeType,
        entityType,
        language,
        countryCode: getCountryCode(),
        successUrl: '',
        errorUrl: '',
        isLppsa: rootState.uiState.isLppsa,
      };

      const relativeUrl = 'Payment';
      const apiUrl = `/application/${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getPaymentStatus = createAsyncThunk(
  'payment/getPaymentStatus',
  async ({
    transactionNo,
    entityType,
  }: {
    transactionNo: string;
    entityType: number;
  }) => {
    try {
      const relativeUrl = `Payment/GetPaymentStatus`;
      const queryString = `?transactionNo=${transactionNo}%entityType=${entityType}`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);
